import {
  CheckCircle,
  ChevronLeft,
  CircleNotifications,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  List,
  Typography,
  useMediaQuery,
} from "@mui/material";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import {
  EnqueueTexterSendMutation,
  EnqueueTexterSendMutationVariables,
  InboxStatsForCampaign,
  InboxType,
  ReleaseAssignmentsMutation,
  ReleaseAssignmentsMutationVariables,
  RequestAssignmentBatchMutation,
  RequestAssignmentBatchMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink, LinkButton } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import {
  RELEASE_ASSIGNMENTS,
  REQUEST_ASSIGNMENT_BATCH,
} from "~frontend/graphql/assignments";
import { ENQUEUE_TEXTER_SEND } from "~frontend/graphql/campaigns";
import { GET_INBOX } from "~frontend/graphql/inboxes";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CampaignListItem, InboxCampaignsTable } from ".";
import { InboxCampaignSummary } from "./InboxCampaignSummary";
import { NonCampaignInboxReplyControls } from "./ReplyControls";
import { GeneralInboxDetails, EscalationInboxDetails } from "./types";

export function NonCampaignInbox({
  inbox,
}: {
  inbox: GeneralInboxDetails | EscalationInboxDetails;
}): ReactElement {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { subaccountLink, subaccount } = useSubaccount()!;
  const isNarrow = useMediaQuery(theme.breakpoints.down("wide"));

  useBreadcrumbs(
    [
      {
        label: subaccount.name,
        path: subaccountLink("/inboxes"),
        backArrow: true,
      },
      {
        label: inbox.inboxName!,
        path: subaccountLink(`/inboxes/${inbox.id}`),
      },
    ],
    { hideSubaccountTitle: true }
  );

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const [requestAssignmentBatch, { loading: requestAssignmentBatchLoading }] =
    useMutation<
      RequestAssignmentBatchMutation,
      RequestAssignmentBatchMutationVariables
    >(REQUEST_ASSIGNMENT_BATCH, {
      refetchQueries: [GET_INBOX],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error claiming replies",
        description: "Error message for claiming replies",
      }),
    });

  const [releaseAssignments, { loading: releaseAssignmentsLoading }] =
    useMutation<
      ReleaseAssignmentsMutation,
      ReleaseAssignmentsMutationVariables
    >(RELEASE_ASSIGNMENTS, {
      refetchQueries: [GET_INBOX],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error releasing replies",
        description: "Error message for releasing replies",
      }),
    });

  const assignmentRequestInProgress =
    requestAssignmentBatchLoading || releaseAssignmentsLoading;

  const handleRequestAssignmentBatch = useCallback(
    async ({ campaignId }: { campaignId: string | null }) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const requestAssignmentBatchResponse = await requestAssignmentBatch({
        variables: { input: { inboxId: inbox.id, campaignId } },
      });

      if (
        requestAssignmentBatchResponse.errors ||
        !requestAssignmentBatchResponse.data
      ) {
        console.error(requestAssignmentBatchResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error claiming replies",
            description: "Error message for claiming replies",
          })
        );
        return;
      }
    },
    [assignmentRequestInProgress, requestAssignmentBatch, inbox, snackbar, intl]
  );

  const handleReleaseAssignments = useCallback(
    async ({ campaignId }: { campaignId?: string | null }) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const releaseAssignmentsResponse = await releaseAssignments({
        variables: {
          input: { subaccountId: subaccount.id, inboxId: inbox.id, campaignId },
        },
      });

      if (
        releaseAssignmentsResponse.errors ||
        !releaseAssignmentsResponse.data
      ) {
        console.error(releaseAssignmentsResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error releasing replies",
            description: "Error message for releasing replies",
          })
        );
        return;
      }
    },
    [
      assignmentRequestInProgress,
      releaseAssignments,
      subaccount,
      inbox,
      snackbar,
      intl,
    ]
  );

  const [enqueuingTexterSendCampaignIds, setEnqueuingTexterSendCampaignIds] =
    useState<string[]>([]);

  const [enqueueTexterSend] = useMutation<
    EnqueueTexterSendMutation,
    EnqueueTexterSendMutationVariables
  >(ENQUEUE_TEXTER_SEND, {
    refetchQueries: [GET_INBOX],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Error sending initials",
      description: "Error message for sending initial messages",
    }),
  });

  const handleEnqueueTexterSend = useCallback(
    async (campaignId: string) => {
      if (enqueuingTexterSendCampaignIds.includes(campaignId)) {
        return;
      }

      setEnqueuingTexterSendCampaignIds([
        ...enqueuingTexterSendCampaignIds,
        campaignId,
      ]);

      try {
        const { data, errors } = await enqueueTexterSend({
          variables: { input: { campaignId } },
        });

        if (errors || !data) {
          snackbar.enqueueSnackbar(
            intl.formatMessage({
              defaultMessage: "Error sending initials",
              description: "Error message for sending initial messages",
            })
          );
        }
      } finally {
        setEnqueuingTexterSendCampaignIds(
          enqueuingTexterSendCampaignIds.filter(cid => cid !== campaignId)
        );
      }
    },
    [
      enqueuingTexterSendCampaignIds,
      setEnqueuingTexterSendCampaignIds,
      enqueueTexterSend,
      snackbar,
      intl,
    ]
  );

  const {
    numRepliesAvailable: totalAvailable,
    numRepliesAssigned: totalAssigned,
  } = inbox.stats;

  const inboxes = inbox.statsByCampaign
    .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    .map((stats, i) => ({
      // table row ids must be strings, so we need to handle
      // null campaign id stats
      id: stats.campaign?.id ?? `null-campaign-id-${i}`,
      statsByCampaign: stats,
    }));

  return (
    <PageContainer maxWidth="md" role="main">
      <InternalLink
        to={subaccountLink(`/inboxes`)}
        sx={{ textDecoration: "none", color: palette.darkGray }}
      >
        <FlexBox alignItems="center">
          {/*Use a negative value to cut off padding from SVG.*/}
          <ChevronLeft sx={{ marginLeft: -1 }} />
          <Typography variant="body2Bold">
            <FormattedMessage
              defaultMessage="Back to Your Inboxes"
              description="Back to inboxes link label"
            />
          </Typography>
        </FlexBox>
      </InternalLink>
      <FlexBox
        justifyContent="space-between"
        direction={isNarrow ? "column" : "row"}
      >
        <PageHeader>{inbox.inboxName!}</PageHeader>
        <FlexBox
          flexDirection="column"
          alignItems={isNarrow ? "flex-start" : "flex-end"}
        >
          <Typography variant="body1Bold">
            {inbox.inboxType === InboxType.General ? (
              <FlexBox alignItems="center" gap="6px">
                <FormattedMessage
                  defaultMessage="Your Replies: {totalAssigned}"
                  description="Alert for the number of assignments"
                  values={{ totalAssigned }}
                />
                {totalAssigned > 0 ? (
                  <CircleNotifications color="error" sx={{ width: "18px" }} />
                ) : (
                  <CheckCircle color="success" sx={{ width: "18px" }} />
                )}
              </FlexBox>
            ) : (
              <FlexBox alignItems="center" gap="6px">
                <FormattedMessage
                  defaultMessage="Escalations: {totalAvailable}"
                  description="Alert for the number of escalations"
                  values={{ totalAvailable }}
                />
                {totalAvailable > 0 ? (
                  <CircleNotifications color="error" sx={{ width: "18px" }} />
                ) : (
                  <CheckCircle color="success" sx={{ width: "18px" }} />
                )}
              </FlexBox>
            )}
          </Typography>
          <InternalLink to={subaccountLink(`/inboxes/${inbox.id}/campaigns`)}>
            <Typography variant="body3" sx={{ display: "block" }}>
              <FormattedMessage
                defaultMessage="View all your replies for this inbox"
                description="Button text for viewing all replies"
              />
            </Typography>
          </InternalLink>
        </FlexBox>
      </FlexBox>
      {isNarrow ? (
        <Box>
          <Divider sx={{ py: 1 }} />
          <List disablePadding>
            {inbox.statsByCampaign.map(statsByCampaign => {
              return (
                <CampaignListItem
                  startedAt={statsByCampaign.campaign?.startedAt}
                  name={statsByCampaign.campaign?.name}
                  description={statsByCampaign.campaign?.description}
                  repliesAssigned={statsByCampaign.assignmentCount}
                  repliesAvailable={statsByCampaign.availableCount}
                  replyControls={
                    <NonCampaignInboxReplyControls
                      statsByCampaign={statsByCampaign as InboxStatsForCampaign}
                      inboxType={InboxType.Escalation}
                      inboxId={inbox.id}
                      enqueuingTexterSendCampaignIds={
                        enqueuingTexterSendCampaignIds
                      }
                      handleEnqueueTexterSend={handleEnqueueTexterSend}
                      handleReleaseAssignments={handleReleaseAssignments}
                      handleRequestAssignmentBatch={
                        handleRequestAssignmentBatch
                      }
                    />
                  }
                />
              );
            })}
          </List>
          <FlexBox
            justifyContent="flex-end"
            sx={{
              marginTop: { narrow: 3, wide: 1 },
              display: totalAssigned === 0 ? "none" : "flex",
            }}
          >
            <Button
              onClick={() => handleReleaseAssignments({})}
              color="accessory"
              variant="contained"
              sx={{ textWrap: "wrap", lineHeight: 1.4, height: "auto" }}
            >
              <FormattedMessage
                defaultMessage="Release All My Replies For The Day"
                description="Button text for releasing all replies"
              />
            </Button>
          </FlexBox>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: palette.white,
            boxShadow: `0px 1px 5px 0px #6A678333`,
            padding: "40px",
            marginTop: theme.spacing(2),
          }}
        >
          <InboxCampaignsTable
            inboxes={inboxes}
            inboxType={inbox.inboxType}
            renderCampaignColumn={({ statsByCampaign }) => {
              switch (inbox.inboxType) {
                case InboxType.General:
                  return (
                    <InboxCampaignSummary
                      campaign={statsByCampaign.campaign}
                      showTexterSendInfo={true}
                    />
                  );
                case InboxType.Escalation:
                  return (
                    <InboxCampaignSummary
                      campaign={statsByCampaign.campaign}
                      showTexterSendInfo={false}
                    />
                  );
                default:
                  return (<></>) as never;
              }
            }}
            renderAvailableRepliesColumn={({ statsByCampaign }) => (
              <FormattedNumber value={statsByCampaign.availableCount} />
            )}
            renderYourRepliesColumn={({ statsByCampaign }) => (
              <FormattedNumber value={statsByCampaign.assignmentCount} />
            )}
            renderActionsColumn={({ statsByCampaign }) => (
              <NonCampaignInboxReplyControls
                statsByCampaign={statsByCampaign as InboxStatsForCampaign}
                inboxType={InboxType.Escalation}
                inboxId={inbox.id}
                enqueuingTexterSendCampaignIds={enqueuingTexterSendCampaignIds}
                handleEnqueueTexterSend={handleEnqueueTexterSend}
                handleReleaseAssignments={handleReleaseAssignments}
                handleRequestAssignmentBatch={handleRequestAssignmentBatch}
              />
            )}
            totalCount={inbox.statsByCampaign.length}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={setRowsPerPage}
            page={page}
            onChangePage={setPage}
            noFooter={inbox.statsByCampaign.length === 0}
            footerActions={
              <LinkButton
                variant="body2"
                onClick={() => handleReleaseAssignments({})}
                sx={{ display: totalAssigned === 0 ? "none" : "auto" }}
              >
                <FormattedMessage
                  defaultMessage="Release All My Threads"
                  description="Button text for releasing all threads"
                />
              </LinkButton>
            }
            reverseFooterOrder
            emptyState={
              inbox.inboxType === InboxType.Escalation
                ? intl.formatMessage({
                    defaultMessage: "There are no escalations in this inbox",
                    description: "Message for an empty escalation inbox",
                  })
                : intl.formatMessage({
                    defaultMessage:
                      "There are no available replies in this inbox",
                    description: "Message for an empty general inbox",
                  })
            }
          />
        </Box>
      )}
    </PageContainer>
  );
}
